
import {
  ConnectionListResult,
  FeatureFlagsContext,
  GridSortProps,
  PaginatedQueryWrapper,
} from '@tableau/data-catalog-shared';
import * as React from 'react';
import { Messages } from '../../../gen-src/Localize';
import { MetricsDisplayGridRowDataFragment } from '../../../gen-src/queries/MetricsDisplayGridRowDataFragment';
import { MetricsDisplayGridRowDataFragmentWithoutPersonalSpace } from '../../../gen-src/queries/MetricsDisplayGridRowDataFragmentWithoutPersonalSpace';
import {
  TableDownstreamMetricsFilteredByColumnQuery,
  TableDownstreamMetricsFilteredByColumnQuery_columns_downstreamMetricsConnection_nodes,
  TableDownstreamMetricsFilteredByColumnQueryVariables,
} from '../../../gen-src/queries/TableDownstreamMetricsFilteredByColumnQuery';
import {
  TableDownstreamMetricsFilteredByColumnWithoutPersonalSpaceQuery,
  TableDownstreamMetricsFilteredByColumnWithoutPersonalSpaceQuery_columns_downstreamMetricsConnection_nodes,
  TableDownstreamMetricsFilteredByColumnWithoutPersonalSpaceQueryVariables,
} from '../../../gen-src/queries/TableDownstreamMetricsFilteredByColumnWithoutPersonalSpaceQuery';
import {
  TableDownstreamMetricsQuery,
  TableDownstreamMetricsQueryVariables,
} from '../../../gen-src/queries/TableDownstreamMetricsQuery';
import {
  TableDownstreamMetricsWithoutPersonalSpaceQuery,
  TableDownstreamMetricsWithoutPersonalSpaceQueryVariables,
} from '../../../gen-src/queries/TableDownstreamMetricsWithoutPersonalSpaceQuery';
import { MetricSortOrder } from '../../../gen-src/queries/globalTypes';
import { TableDownstreamMetricsFilteredByColumnQueryGql } from '../../queries/TableDownstreamMetricsFilteredByColumnQueryGql';
import { TableDownstreamMetricsFilteredByColumnWithoutPersonalSpaceQueryGql } from '../../queries/TableDownstreamMetricsFilteredByColumnWithoutPersonalSpaceQueryGql';
import { TableDownstreamMetricsQueryGql } from '../../queries/TableDownstreamMetricsQueryGql';
import { TableDownstreamMetricsWithoutPersonalSpaceQueryGql } from '../../queries/TableDownstreamMetricsWithoutPersonalSpaceQueryGql';
import { getRowDataForMetric, getRowDataForMetricWithoutPersonalSpace, MetricsDisplayGrid, MetricsDisplayGridRowData } from '../sharedDisplayGrid/MetricsDisplayGrid';
import { MetricsValidSortFields } from '../sorting/sortProviders/MetricsGridSortProvider';

type QueryVariables = TableDownstreamMetricsQueryVariables;
type QueryResults = TableDownstreamMetricsQuery;
type QueryResultListNode = MetricsDisplayGridRowDataFragment;
type GridRowData = MetricsDisplayGridRowData;

type QueryVariablesWithoutPersonalSpace = TableDownstreamMetricsWithoutPersonalSpaceQueryVariables;
type QueryResultsWithoutPersonalSpace = TableDownstreamMetricsWithoutPersonalSpaceQuery;
type QueryResultListNodeWithoutPersonalSpace = MetricsDisplayGridRowDataFragmentWithoutPersonalSpace;

type QueryVariablesWithFilter = TableDownstreamMetricsFilteredByColumnQueryVariables;
type QueryResultsWithFilter = TableDownstreamMetricsFilteredByColumnQuery;
type QueryResultListNodeWithFilter = TableDownstreamMetricsFilteredByColumnQuery_columns_downstreamMetricsConnection_nodes;

type QueryVariablesWithFilterWithoutPersonalSpace = TableDownstreamMetricsFilteredByColumnWithoutPersonalSpaceQueryVariables;
type QueryResultsWithFilterWithoutPersonalSpace = TableDownstreamMetricsFilteredByColumnWithoutPersonalSpaceQuery;
type QueryResultListNodeWithFilterWithoutPersonalSpace =
  TableDownstreamMetricsFilteredByColumnWithoutPersonalSpaceQuery_columns_downstreamMetricsConnection_nodes;

function selectConnectionListResult(resultData: QueryResults): ConnectionListResult<QueryResultListNode> | null {
  return resultData && resultData.tables && resultData.tables[0] &&
    resultData.tables[0].downstreamMetricsConnection;
}

function selectConnectionListResultWithoutPersonalSpace(
  resultData: QueryResultsWithoutPersonalSpace
): ConnectionListResult<QueryResultListNodeWithoutPersonalSpace> | null {
  return resultData && resultData.tables && resultData.tables[0] &&
    resultData.tables[0].downstreamMetricsConnection;
}

function selectConnectionListResultForFilteredQuery(
  resultData: QueryResultsWithFilter
): ConnectionListResult<QueryResultListNodeWithFilter> | null {
  return resultData && resultData.columns && resultData.columns[0] &&
    resultData.columns[0].downstreamMetricsConnection;
}

function selectConnectionListResultForFilteredQueryWithoutPersonalSpace(
  resultData: QueryResultsWithFilterWithoutPersonalSpace
): ConnectionListResult<QueryResultListNodeWithFilterWithoutPersonalSpace> | null {
  return resultData && resultData.columns && resultData.columns[0] &&
    resultData.columns[0].downstreamMetricsConnection;
}

export type TableDownstreamMetricsQueryGridProps =
  GridSortProps<MetricsValidSortFields> & {
    readonly tableId: string;
    readonly filteredColumnId: string | null;
    readonly rowCount: number | null;
  };

export function TableDownstreamMetricsQueryGrid(props: TableDownstreamMetricsQueryGridProps): JSX.Element {
  const { personalSpaceEnabled } = React.useContext(FeatureFlagsContext);
  const emptyGridMessage = Messages.EmptyDownstreamMetricsLineageGridMessage();
  const emptyFilteredGridMessage = Messages.EmptyDownstreamFilteredMetricsLineageGridMessage();

  return personalSpaceEnabled ? (
    props.filteredColumnId ? (
      <PaginatedQueryWrapper<QueryVariablesWithFilter, QueryResultsWithFilter, QueryResultListNodeWithFilter, GridRowData>
        queryGql={TableDownstreamMetricsFilteredByColumnQueryGql}
        partialQueryVariables={{ columnId: props.filteredColumnId, orderBy: props.orderBy as MetricSortOrder }}
        selectConnectionListResult={selectConnectionListResultForFilteredQuery}
        mapResultNode={getRowDataForMetric}
      >
        {({ rowStore }) => <MetricsDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyFilteredGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />}
      </PaginatedQueryWrapper>
    ) : (
        <PaginatedQueryWrapper<QueryVariables, QueryResults, QueryResultListNode, GridRowData>
          queryGql={TableDownstreamMetricsQueryGql}
          partialQueryVariables={{ tableId: props.tableId, orderBy: props.orderBy as MetricSortOrder }}
          selectConnectionListResult={selectConnectionListResult}
          mapResultNode={getRowDataForMetric}
        >
          {({ rowStore }) => <MetricsDisplayGrid
            rowStore={rowStore}
            emptyGridMessage={emptyGridMessage}
            rowCount={props.rowCount}
            orderBy={props.orderBy}
            updateSortOrder={props.updateSortOrder}
          />}
        </PaginatedQueryWrapper>
    )
  ) : (
    props.filteredColumnId ? (
      <PaginatedQueryWrapper<QueryVariablesWithFilterWithoutPersonalSpace, QueryResultsWithFilterWithoutPersonalSpace,
      QueryResultListNodeWithFilterWithoutPersonalSpace, GridRowData>
        queryGql={TableDownstreamMetricsFilteredByColumnWithoutPersonalSpaceQueryGql}
        partialQueryVariables={{ columnId: props.filteredColumnId, orderBy: props.orderBy as MetricSortOrder }}
        selectConnectionListResult={selectConnectionListResultForFilteredQueryWithoutPersonalSpace}
        mapResultNode={getRowDataForMetricWithoutPersonalSpace}
      >
        {({ rowStore }) => <MetricsDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyFilteredGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />}
      </PaginatedQueryWrapper>
    ) : (
        <PaginatedQueryWrapper<QueryVariablesWithoutPersonalSpace, QueryResultsWithoutPersonalSpace,
        QueryResultListNodeWithoutPersonalSpace, GridRowData>
          queryGql={TableDownstreamMetricsWithoutPersonalSpaceQueryGql}
          partialQueryVariables={{ tableId: props.tableId, orderBy: props.orderBy as MetricSortOrder }}
          selectConnectionListResult={selectConnectionListResultWithoutPersonalSpace}
          mapResultNode={getRowDataForMetricWithoutPersonalSpace}
        >
          {({ rowStore }) => <MetricsDisplayGrid
            rowStore={rowStore}
            emptyGridMessage={emptyGridMessage}
            rowCount={props.rowCount}
            orderBy={props.orderBy}
            updateSortOrder={props.updateSortOrder}
          />}
        </PaginatedQueryWrapper>
    )
  );
}
